import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { afterExit, animationLength, exitProgress, resumeEntry } from '@/lib/exit-motion';

type Phase = 'open' | 'closing' | 'gone';

/**
 * Keeps a layer that owns its open state mounted through its exit animation (lib/exit-motion.ts).
 *
 *   const { mounted, state, ref } = usePresence<HTMLDivElement>(open);
 *   return mounted && <div ref={ref} data-state={state}>…</div>;
 *
 * `state` is `'closed'` from the render that closes it; the layer goes on its own `animationend`, or at the
 * animation's length plus 50 ms, or at once when nothing animates. Reopened before it has gone, it is the same
 * element — no second mount — and its entry starts where the exit had got to. The stylesheet should give a
 * closing layer `pointer-events: none` (motion.css does), so the page is the user's the moment a close starts.
 */
export function usePresence<T extends HTMLElement>(open: boolean): {
  mounted: boolean;
  state: 'open' | 'closed';
  ref: RefObject<T | null>;
} {
  const [phase, setPhase] = useState<Phase>(open ? 'open' : 'gone');
  const ref = useRef<T | null>(null);
  /** When the running exit started and how long it runs, for a reopen to turn round from. */
  const exit = useRef<{ at: number; length: number } | null>(null);

  // Derived during render, so the closing render already says `closed` and a reopen never renders `gone`.
  if (open && phase !== 'open') setPhase('open');
  if (!open && phase === 'open') setPhase('closing');

  useLayoutEffect(() => {
    const el = ref.current;
    if (phase === 'open') {
      const was = exit.current;
      exit.current = null;
      if (was !== null) resumeEntry([el], exitProgress(was.at, was.length));
      return undefined;
    }
    if (phase !== 'closing') return undefined;
    if (el === null) {
      setPhase('gone');
      return undefined;
    }
    exit.current = { at: performance.now(), length: animationLength(el) ?? 0 };
    return afterExit(el, () => {
      exit.current = null;
      setPhase('gone');
    });
  }, [phase]);

  return { mounted: phase !== 'gone', state: phase === 'open' ? 'open' : 'closed', ref };
}
